import { Rpc } from '@lightprotocol/stateless.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { ComputeBudgetProgram, PublicKey, Transaction } from '@solana/web3.js';

/**
 * Builds the transaction for decompression.
 *
 * It adds compute budget instructions, conditionally adds an ATA creation instruction if needed,
 * and appends the decompress instruction. It also sets the fee payer and recent blockhash.
 *
 * @param publicKey - The public key of the user.
 * @param ata - The associated token address.
 * @param decompressIx - The decompress instruction.
 * @param connection - The RPC connection instance.
 * @param mintAddress - The token mint address.
 * @returns An object containing the built transaction, blockhash, and last valid block height.
 */
export const buildTransaction = async (
  publicKey: PublicKey,
  ata: PublicKey,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  decompressIx: any,
  connection: Rpc,
  mintAddress: PublicKey,
) => {
  const accountInfo = await connection.getAccountInfo(ata);
  const transaction = new Transaction();

  transaction.add(
    ComputeBudgetProgram.setComputeUnitLimit({ units: 1000000 }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 1000000 }),
  );

  if (!accountInfo) {
    console.log('Adding ATA creation instruction...');
    const createAtaIx = createAssociatedTokenAccountInstruction(
      publicKey,
      ata,
      publicKey,
      mintAddress,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID,
    );
    transaction.add(createAtaIx);
  }

  transaction.add(decompressIx);
  transaction.feePayer = publicKey;

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash();
  transaction.recentBlockhash = blockhash;

  return { transaction, blockhash, lastValidBlockHeight };
};
